'use client';

import * as React from 'react';
import { LuX } from 'react-icons/lu';

import { Dialog } from './modal';
import { Box } from './box';
import { Text } from './text';
import { HStack } from './stack';
import { IconButton } from './icon-button';

/**
 * FormModal component props
 */
export interface FormModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when modal closes
   */
  onClose: () => void;

  /**
   * Modal title
   */
  title: string;

  /**
   * Optional subtitle/description
   */
  subtitle?: string;

  /**
   * Modal content (typically a form component)
   */
  children: React.ReactNode;

  /**
   * Responsive size configuration
   * @default {{ base: 'full', md: 'xl' }}
   */
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | 'cover'
    | 'full'
    | {
        base: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'cover' | 'full';
        md: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'cover' | 'full';
      };

  /**
   * Scroll behavior - inside modal or outside
   * @default 'inside'
   */
  scrollBehavior?: 'inside' | 'outside';

  /**
   * Motion preset for animation
   * @default 'slide-in-bottom'
   */
  motionPreset?:
    | 'scale'
    | 'slide-in-bottom'
    | 'slide-in-top'
    | 'slide-in-left'
    | 'slide-in-right'
    | 'none';

  /**
   * Maximum height for modal body
   * @default '70vh'
   */
  maxBodyHeight?: string;

  /**
   * Custom aria label for close button
   * @default 'Close'
   */
  closeAriaLabel?: string;

  /**
   * Whether to prevent closing on outside click
   * @default false
   */
  preventClose?: boolean;
}

/**
 * @deprecated Use MultiStepForm instead for consistent form UX across the project
 *
 * FormModal is being phased out in favor of the unified MultiStepForm pattern.
 * MultiStepForm provides:
 * - Automatic Card wrapper for consistent styling
 * - Built-in navigation buttons (Previous/Next/Submit/Cancel)
 * - Single-step and multi-step wizard support
 * - Unified form behavior and validation
 *
 * ## Migration Guide
 *
 * ### Old Pattern (FormModal):
 * ```tsx
 * <FormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Add Vehicle"
 *   subtitle="Add a new vehicle to your fleet"
 * >
 *   <VehicleForm onSuccess={handleSuccess} onCancel={onClose} />
 * </FormModal>
 * ```
 *
 * ### New Pattern (MultiStepForm single-step):
 * ```tsx
 * <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
 *   <Dialog.Backdrop />
 *   <Dialog.Positioner>
 *     <Dialog.Content maxWidth="6xl">
 *       <Dialog.Header>
 *         <Dialog.Title>Add Vehicle</Dialog.Title>
 *         <Dialog.CloseTrigger />
 *       </Dialog.Header>
 *       <Dialog.Body maxHeight="70vh" overflowY="auto">
 *         <MultiStepForm
 *           steps={[
 *             {
 *               id: 'vehicle-info',
 *               title: 'Vehicle Information',
 *               description: 'Add a new vehicle to your fleet',
 *               schema: vehicleFormSchema,
 *               component: VehicleFormStep,
 *             },
 *           ]}
 *           defaultValues={defaultValues}
 *           onSubmit={handleSubmit}
 *           mode="single"
 *           showProgress={false}
 *           submitButtonText="Create Vehicle"
 *           onCancel={onClose}
 *         />
 *       </Dialog.Body>
 *     </Dialog.Content>
 *   </Dialog.Positioner>
 * </Dialog.Root>
 * ```
 *
 * ## Benefits of Migration
 * - ✅ Consistent Card wrapper automatically applied
 * - ✅ Professional navigation buttons built-in
 * - ✅ Single source of truth for form behavior
 * - ✅ Easy to upgrade from single-step to multi-step wizard
 * - ✅ Better DRY compliance
 *
 * @see MultiStepForm for the replacement component
 * @see apps/web/src/components/vehicles/vehicle-form-modal.tsx for migration example
 * @see apps/web/src/components/drivers/driver-form-modal.tsx for migration example
 *
 * FormModal - Legacy modal component for forms (DEPRECATED)
 *
 * Provides consistent styling and behavior for all form modals:
 * - Styled backdrop with blur effect
 * - Proper positioning and centering
 * - Responsive sizing (fullscreen on mobile, xl on desktop)
 * - Internal scrolling for long forms
 * - Smooth slide-in animation
 * - Standardized header with title, subtitle, and close button
 *
 * @example (DEPRECATED - Use MultiStepForm instead)
 * ```tsx
 * <FormModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Add New Order"
 *   subtitle="Create a new order for your transportation needs"
 * >
 *   <OrderForm onSubmit={handleSubmit} />
 * </FormModal>
 * ```
 */
export function FormModal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  size = { base: 'full', md: 'xl' },
  scrollBehavior = 'inside',
  motionPreset = 'slide-in-bottom',
  maxBodyHeight = '70vh',
  closeAriaLabel = 'Close',
  preventClose = false,
}: FormModalProps) {
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }: { open: boolean }) => !open && !preventClose && onClose()}
      size={size}
      scrollBehavior={scrollBehavior}
      motionPreset={motionPreset}
      closeOnInteractOutside={!preventClose}
      closeOnEscape={!preventClose}
    >
      {/* Styled backdrop - Chakra UI v3 provides default styling */}
      <Dialog.Backdrop />

      {/* Positioner for proper centering - RULE 15: Proper Chakra UI v3 pattern with type assertion */}
      <Dialog.Positioner {...({} as React.ComponentProps<typeof Dialog.Positioner>)}>
        <Dialog.Content>
          {/* Header with title, subtitle, and close button */}
          <Dialog.Header paddingBottom={4}>
            <HStack justifyContent="space-between" alignItems="center">
              <Box>
                <Text fontSize="xl" fontWeight="bold">
                  {title}
                </Text>
                {subtitle && (
                  <Text fontSize="sm" color="#6B7280" fontWeight="normal">
                    {subtitle}
                  </Text>
                )}
              </Box>
              {!preventClose && (
                <IconButton
                  aria-label={closeAriaLabel}
                  icon={<LuX />}
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                />
              )}
            </HStack>
          </Dialog.Header>

          {/* Body with scroll capability */}
          <Dialog.Body maxHeight={maxBodyHeight} overflowY="auto" paddingBottom={6}>
            {children}
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
